import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { useState } from "react";
import { NoteObservable } from "@/store/note/NoteObservable";
import { toastModule } from "@/modules/toast";
import {
  MDSIconButton,
  MDSIconKind,
  MDSText,
  MDSTextFieldVariant,
  MDSTextField,
  MDSTextVariant,
  MDSIconButtonSize,
} from "@/mds";

interface NoteSuggestionRefineActionsProps {
  noteObservable: NoteObservable;
  setShowRefineInput: (show: boolean) => void;
}

export const NoteSuggestionRefineActions = observer(function NoteSuggestionRefineActions({
  noteObservable,
  setShowRefineInput,
}: NoteSuggestionRefineActionsProps) {
  const [refineText, setRefineText] = useState("");
  const trimmedRefineText = refineText.trim();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!trimmedRefineText || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await noteObservable.refineNoteSuggestion(trimmedRefineText);
      setShowRefineInput(false);
    } catch {
      toastModule.triggerToast({ content: "Failed to refine note. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowRefineInput(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };
  return (
    <StyledContainer>
      <MDSText variant={MDSTextVariant.TextSm}>Refine edits</MDSText>
      <Expanding>
        <MDSTextField
          variant={MDSTextFieldVariant.Emphasis}
          value={refineText}
          onChange={setRefineText}
          onKeyDown={handleKeyDown}
          placeholder="Describe how you want to refine the edits."
          isDisabled={isSubmitting}
          autoFocus
        />
      </Expanding>
      <StyledSubmitIconButton
        iconKind={MDSIconKind.Send}
        onClick={handleSubmit}
        isDisabled={isSubmitting}
        isLoading={isSubmitting}
        size={MDSIconButtonSize.Medium}
      />
      <MDSIconButton
        onClick={handleCancel}
        iconKind={MDSIconKind.Exit}
        size={MDSIconButtonSize.Medium}
      />
    </StyledContainer>
  );
});

const StyledContainer = styled.div`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  background: ${({ theme }) => theme.colors.surface.modal};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  padding: ${({ theme }) => theme.spacing[4]};
  gap: ${({ theme }) => theme.spacing[2]};
  width: 95vw;
  max-width: 600px;
`;

const Expanding = styled.div`
  flex: 1;
`;

const StyledSubmitIconButton = styled(MDSIconButton)`
  & .mds-icon {
    color: ${({ theme }) => theme.colors.icon.emphasis};
  }
`;
